/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { produce } from 'immer';
import {
  type BotInfoForUpdate,
  type ContextMode,
  type GetDraftBotInfoAgwData,
  OpenAIApiMode,
  ReasoningEffort,
} from '@adp-arch/idl/playground_api';
import { ContextContentType } from '@adp-arch/bot-api/developer_api';

import {
  type SetterAction,
  setterActionFactory,
} from '../utils/setter-factory';
import { type BotDetailModel, type PersistedModelConfig } from '../types/model';

export type { PersistedModelConfig } from '../types/model';

type ModelInfoForUpdate = NonNullable<BotInfoForUpdate['model_info']>;
interface ModelInfoWithLegacyParameters {
  parameters?: Record<string, string>;
  /** Older backend versions serialized this field using the Thrift name. */
  // eslint-disable-next-line @typescript-eslint/naming-convention -- legacy backend JSON field
  Parameters?: Record<string, string>;
}

export const DEFAULT_REACT_MAX_STEPS = 30;
export const MIN_REACT_MAX_STEPS = 1;
export const MAX_REACT_MAX_STEPS = 100;

export const DEFAULT_SINGLE_AGENT_TIMEOUT_MINUTES = 5;
export const DEFAULT_SINGLE_AGENT_VIDEO_TIMEOUT_MINUTES = 45;
export const MIN_SINGLE_AGENT_TIMEOUT_MINUTES = 1;

export const normalizeReactMaxSteps = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);

  return Number.isInteger(parsed) &&
    parsed >= MIN_REACT_MAX_STEPS &&
    parsed <= MAX_REACT_MAX_STEPS
    ? parsed
    : DEFAULT_REACT_MAX_STEPS;
};

export const normalizeSingleAgentTimeoutMinutes = (
  value: unknown,
  defaultValue = DEFAULT_SINGLE_AGENT_TIMEOUT_MINUTES,
): number => {
  const parsed = typeof value === 'number' ? value : Number(value);

  return Number.isSafeInteger(parsed) &&
    parsed >= MIN_SINGLE_AGENT_TIMEOUT_MINUTES
    ? parsed
    : defaultValue;
};

const normalizeModelParameters = (
  parameters?: Record<string, string>,
): Record<string, string> => {
  const normalized: Record<string, string> = {
    ...parameters,
    react_max_steps: String(normalizeReactMaxSteps(parameters?.react_max_steps)),
  };
  const configuredTimeout = Number(parameters?.single_agent_timeout_minutes);

  if (
    Number.isSafeInteger(configuredTimeout) &&
    configuredTimeout >= MIN_SINGLE_AGENT_TIMEOUT_MINUTES
  ) {
    normalized.single_agent_timeout_minutes = String(configuredTimeout);
  } else {
    delete normalized.single_agent_timeout_minutes;
  }

  return normalized;
};

const getModelParameters = (
  modelInfo?: ModelInfoWithLegacyParameters,
): Record<string, string> | undefined =>
  modelInfo?.parameters ?? modelInfo?.Parameters;

const API_MODE_LABEL_MAP: Record<string, OpenAIApiMode> = {
  chat: OpenAIApiMode.ChatCompletions,
  chat_completions: OpenAIApiMode.ChatCompletions,
  chatcompletions: OpenAIApiMode.ChatCompletions,
  responses: OpenAIApiMode.Responses,
};

const REASONING_EFFORT_LABEL_MAP: Record<string, ReasoningEffort> = {
  none: ReasoningEffort.None,
  minimal: ReasoningEffort.Minimal,
  low: ReasoningEffort.Low,
  medium: ReasoningEffort.Medium,
  high: ReasoningEffort.High,
  xhigh: ReasoningEffort.XHigh,
  x_high: ReasoningEffort.XHigh,
};

const normalizeNumericEnumValue = <T extends number>(
  value: unknown,
  labelMap: Record<string, T>,
  fieldName: string,
): T | undefined => {
  if (value === null || typeof value === 'undefined') {
    return undefined;
  }

  if (typeof value === 'number') {
    return value as T;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return undefined;
    }

    const numericValue = Number(trimmed);

    if (Number.isInteger(numericValue)) {
      return numericValue as T;
    }

    const labelValue = labelMap[trimmed.toLowerCase()];

    if (typeof labelValue !== 'undefined') {
      return labelValue;
    }
  }

  throw new Error(`Invalid ${fieldName}: ${String(value)}`);
};

const normalizeAPIMode = (
  value: PersistedModelConfig['api_mode'] | string | null | undefined,
): ModelInfoForUpdate['api_mode'] =>
  normalizeNumericEnumValue(value, API_MODE_LABEL_MAP, 'api_mode');

const normalizeReasoningEffort = (
  value: PersistedModelConfig['reasoning_effort'] | string | null | undefined,
): ModelInfoForUpdate['reasoning_effort'] =>
  normalizeNumericEnumValue(
    value,
    REASONING_EFFORT_LABEL_MAP,
    'reasoning_effort',
  );

export const DEFAULT_MODEL_INFO = (): PersistedModelConfig => ({
  model: '',
  temperature: 0,
  max_tokens: 4096,
  top_p: 0,
  frequency_penalty: 0,
  presence_penalty: 0,
  prompt_id: 0,
  ShortMemPolicy: {
    ContextContentType: ContextContentType.USER_RES,
  },
  card_ids: [],
  parameters: normalizeModelParameters(),
});
export const getDefaultModelStore = (): ModelStore => ({
  config: DEFAULT_MODEL_INFO(),
  modelList: [],
});

/** Persona & Prompted Areas */
export type ModelStore = BotDetailModel;

export interface ModelAction {
  setModel: SetterAction<ModelStore>;
  setModelByImmer: (update: (state: ModelStore) => void) => void;
  transformDto2Vo: (botData: GetDraftBotInfoAgwData) => PersistedModelConfig;
  transformVo2Dto: (
    model: PersistedModelConfig,
  ) => BotInfoForUpdate['model_info'];
  initStore: (botData: GetDraftBotInfoAgwData) => void;
  clear: () => void;
}

export const useModelStore = create<ModelStore & ModelAction>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...getDefaultModelStore(),
      setModel: setterActionFactory<ModelStore>(set),
      setModelByImmer: update =>
        set(
          produce<ModelStore>(model => update(model)),
          false,
          'setModelByImmer',
        ),
      transformDto2Vo: botData => {
        const modelInfo = botData.bot_info.model_info;
        const config = botData.bot_option_data?.model_detail_map;
        return {
          model: modelInfo?.model_id,
          temperature: modelInfo?.temperature,
          max_tokens: modelInfo?.max_tokens,
          top_p: modelInfo?.top_p,
          frequency_penalty: modelInfo?.frequency_penalty,
          presence_penalty: modelInfo?.presence_penalty,
          ShortMemPolicy: {
            ContextContentType: modelInfo?.short_memory_policy
              ?.context_mode as unknown as ContextContentType,
            HistoryRound: modelInfo?.short_memory_policy?.history_round,
          },
          api_mode: modelInfo?.api_mode,
          reasoning_effort: modelInfo?.reasoning_effort,
          parameters: normalizeModelParameters(getModelParameters(modelInfo)),
          model_name:
            modelInfo?.model_id && config
              ? config[modelInfo.model_id]?.model_name
              : '',

          model_style: modelInfo?.model_style,
          response_format: modelInfo?.response_format,
        };
      },
      transformVo2Dto: model =>
        model?.model
          ? {
              model_id: model.model,
              temperature: model.temperature,
              max_tokens: model.max_tokens,
              top_p: model.top_p,
              presence_penalty: model.presence_penalty,
              frequency_penalty: model.frequency_penalty,
              short_memory_policy: {
                history_round: model?.ShortMemPolicy?.HistoryRound,
                context_mode: model?.ShortMemPolicy
                  ?.ContextContentType as unknown as ContextMode,
              },
              api_mode: normalizeAPIMode(model.api_mode),
              reasoning_effort: normalizeReasoningEffort(
                model.reasoning_effort,
              ),
              parameters: normalizeModelParameters(model.parameters),
              response_format: model.response_format,
              model_style: model.model_style,
            }
          : {},
      initStore: botData => {
        const { transformDto2Vo } = get();
        const { bot_info, bot_option_data } = botData;
        bot_info?.model_info && bot_option_data?.model_detail_map
          ? set({
              config: transformDto2Vo(botData),
            })
          : set({ config: DEFAULT_MODEL_INFO() });
      },
      clear: () => {
        set({ ...getDefaultModelStore() });
      },
    })),
    {
      enabled: IS_DEV_MODE,
      name: 'botStudio.botDetail.model',
    },
  ),
);
